import { useEffect, useMemo, useRef } from 'react';

import { getBackend } from '@/backend';
import { userHasPermission } from '@/features/auth/logic/contactLinking';
import { DEFAULT_ROLES } from '@/features/auth/permissions';
import { Permission } from '@/features/auth/types';
import { normalizeNoteType } from '@/features/notes/note-type';
import type { Note } from '@/features/notes/types';
import { useAppStore } from '@/store/useAppStore';

import {
  clearAcknowledgedPendingNoteDeletes,
  clearPendingNoteWrite,
  isPendingNoteDelete,
  isPendingNoteWrite,
} from '../note-pending-writes';
import type { ProjectScope } from '../project-scope';

const PENDING_LOCAL_NOTE_WINDOW_MS = 60_000;

interface UseRawNotesSyncOptions {
  canAccessAsOrgAdmin?: boolean;
}

function noteUpdatedAtMs(note: Note): number | null {
  const updatedAtMs = Date.parse(note.updatedAt);
  return Number.isFinite(updatedAtMs) ? updatedAtMs : null;
}

function isRecentLocalNote(note: Note, nowMs: number): boolean {
  const updatedAtMs = noteUpdatedAtMs(note);
  return updatedAtMs !== null
    && nowMs - updatedAtMs >= 0
    && nowMs - updatedAtMs <= PENDING_LOCAL_NOTE_WINDOW_MS;
}

function remoteNoteFreshness(remoteNote: Note, localNote: Note): 'older' | 'same' | 'newer' | 'unknown' {
  const remoteUpdatedAtMs = noteUpdatedAtMs(remoteNote);
  const localUpdatedAtMs = noteUpdatedAtMs(localNote);
  if (remoteUpdatedAtMs === null || localUpdatedAtMs === null) {
    return 'unknown';
  }
  if (remoteUpdatedAtMs > localUpdatedAtMs) {
    return 'newer';
  }
  if (remoteUpdatedAtMs < localUpdatedAtMs) {
    return 'older';
  }
  return 'same';
}

function hasRemoteAcknowledgedOrSupersededPendingNote(remoteNote: Note, localNote: Note): boolean {
  const freshness = remoteNoteFreshness(remoteNote, localNote);
  if (freshness === 'newer') {
    return true;
  }
  if (freshness === 'older') {
    return false;
  }

  return notesMatchForPendingWrite(remoteNote, localNote);
}

function normalizeNoteMetadata(note: Note): Note {
  const type = normalizeNoteType(note.type);
  return note.type === type ? note : { ...note, type };
}

function normalizePendingComparisonValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(normalizePendingComparisonValue);
  }
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([, entryValue]) => entryValue !== undefined)
        .toSorted(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
        .map(([key, entryValue]) => [key, normalizePendingComparisonValue(entryValue)])
    );
  }
  return value;
}

type NoteMetadataForPendingComparison = Omit<Note, 'content'>;

function normalizeNoteMetadataDefaultsForPendingComparison(
  metadata: NoteMetadataForPendingComparison,
): NoteMetadataForPendingComparison {
  const normalized = { ...metadata };
  if (normalized.searchIndex?.length === 0) {
    delete normalized.searchIndex;
  }
  return normalized;
}

function noteMetadataForPendingComparison(note: Note): NoteMetadataForPendingComparison {
  const {
    content: _content,
    ...metadata
  } = note;
  return normalizeNoteMetadataDefaultsForPendingComparison(metadata);
}

function notesMatchForPendingWrite(remoteNote: Note, localNote: Note): boolean {
  return JSON.stringify(normalizePendingComparisonValue(noteMetadataForPendingComparison(remoteNote)))
    === JSON.stringify(normalizePendingComparisonValue(noteMetadataForPendingComparison(localNote)));
}

function mergeNotesWithPendingLocal(
  scope: ProjectScope,
  remoteNotes: Note[],
  localNotes: Note[],
  nowMs = Date.now(),
): Note[] {
  const remoteById = new Map(remoteNotes.map(note => [note.id, note]));
  const mergedById = new Map<string, Note>();

  for (const note of remoteNotes) {
    if (!isPendingNoteDelete(scope, note.id)) {
      mergedById.set(note.id, note);
    }
  }

  for (const note of localNotes) {
    const remoteNote = remoteById.get(note.id);
    const isPendingLocalNote = isPendingNoteWrite(scope, note.id)
      || (!remoteNote && isRecentLocalNote(note, nowMs))
      || (
        remoteNote
        && isRecentLocalNote(note, nowMs)
        && !hasRemoteAcknowledgedOrSupersededPendingNote(remoteNote, note)
      );

    if (isPendingLocalNote && !isPendingNoteDelete(scope, note.id)) {
      mergedById.set(note.id, note);
    }
  }

  return Array.from(mergedById.values());
}

function clearAcknowledgedPendingNoteWrites(
  scope: ProjectScope,
  remoteNotes: Note[],
  localNotes: Note[],
): void {
  const localById = new Map(localNotes.map(note => [note.id, note]));
  const remoteNoteIds = new Set(remoteNotes.map(note => note.id));

  for (const remoteNote of remoteNotes) {
    const localNote = localById.get(remoteNote.id);
    if (!localNote || hasRemoteAcknowledgedOrSupersededPendingNote(remoteNote, localNote)) {
      clearPendingNoteWrite(scope, remoteNote.id);
    }
  }
  clearAcknowledgedPendingNoteDeletes(scope, remoteNoteIds);
}

export function useRawNotesSync(enabled = true, options: UseRawNotesSyncOptions = {}): void {
  const cloudEnabled = useAppStore(state => state.cloud.enabled);
  const orgId = useAppStore(state => state.cloud.orgId);
  const projectId = useAppStore(state => state.cloud.projectId);
  const member = useAppStore(state => state.cloud.member);
  const isAuthInitialized = useAppStore(state => state.auth.initialized);
  const user = useAppStore(state => state.auth.user);
  const roles = useAppStore(state => state.admin.roles);
  const mountedRef = useRef(true);

  const canSyncRawNotes = useMemo(() => (
    user?.isSuperAdmin === true
      || options.canAccessAsOrgAdmin === true
      || userHasPermission(member, roles.length > 0 ? roles : DEFAULT_ROLES, Permission.EDIT_NOTES)
  ), [member, options.canAccessAsOrgAdmin, roles, user?.isSuperAdmin]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (
      !enabled
      || !cloudEnabled
      || !orgId
      || !projectId
      || !isAuthInitialized
      || !user
      || !canSyncRawNotes
    ) {
      return;
    }

    const rawNotes = getBackend().rawNotes;
    if (!rawNotes) {
      throw new Error('RawNotesClient is not configured for the active backend.');
    }

    const scope = { orgId, projectId };
    return rawNotes.subscribeNotes(scope, remoteNotes => {
      if (!mountedRef.current) {
        return;
      }

      const normalizedRemoteNotes = remoteNotes.map(normalizeNoteMetadata);
      const localNotes = (useAppStore.getState().notes ?? []).map(normalizeNoteMetadata);
      clearAcknowledgedPendingNoteWrites(scope, normalizedRemoteNotes, localNotes);
      useAppStore.setState({
        notes: mergeNotesWithPendingLocal(scope, normalizedRemoteNotes, localNotes),
      });
    });
  }, [enabled, cloudEnabled, orgId, projectId, isAuthInitialized, user, canSyncRawNotes]);
}
